// AGENT MODE — the overlays (WS-AGENT-MODE-1): the command palette with
// workspace search, pickers, forms, confirmations and the key help, as real
// DOM dialogs over the frame (smbls cli/tui shell/overlays.js: a titled box
// with its key hint on the bottom edge). The top overlay takes every key in
// the runtime; the DOM shows it and forwards clicks: a row `{ pick }`, a form
// field `{ field }`, the submit button `{ submit }`, yes / no `{ ok }`.

export const AgentModeOverlay = {
  data: { agentRegion: 'overlay' },
  position: 'absolute',
  inset: '0',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: 'D A A',
  background: 'scrim',
  hide: (el, s) => !el.call('agentModeOverlayModel', s.root),

  AgentModeDialog: {
    tag: 'section',
    role: 'dialog',
    aria: {
      modal: 'true',
      label: (el, s) => {
        const o = el.call('agentModeOverlayModel', s.root)
        if (!o) return null
        return o.title
      }
    },
    data: {
      agentOverlay: (el, s) => (el.call('agentModeOverlayModel', s.root) || {}).kind || null
    },
    display: 'flex',
    flexDirection: 'column',
    gap: 'Z',
    width: 'overlayXl',
    maxWidth: '100%',
    maxHeight: '100%',
    boxSizing: 'border-box',
    padding: 'Z A',
    background: 'document',
    borderWidth: 'hairline',
    borderStyle: 'solid',
    borderColor: 'line',
    round: 'radiusCard',
    overflow: 'hidden',

    AgentModeDialogTitle: {
      tag: 'h2',
      margin: '0',
      fontSize: 'inherit',
      fontWeight: '400',
      textTransform: 'uppercase',
      color: 'caption',
      text: (el, s) => {
        const o = el.call('agentModeOverlayModel', s.root)
        return o ? o.title : ''
      }
    },

    AgentModePicker: {},
    AgentModeForm: {},
    AgentModeConfirm: {},
    AgentModeHelp: {},

    AgentModeDialogHint: {
      alignSelf: 'flex-end',
      color: 'caption',
      text: (el, s) => {
        const o = el.call('agentModeOverlayModel', s.root)
        const key = {
          picker: 'workspace.agentMode.hintPicker',
          form: 'workspace.agentMode.hintForm',
          confirm: 'workspace.agentMode.hintConfirm',
          help: 'workspace.agentMode.hintHelp'
        }[(o && o.kind) || '']
        return key ? el.call('polyglot', key) : ''
      }
    }
  }
}

// The palette and every picker: a query field, then the matching rows.
export const AgentModePicker = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '0',
  hide: (el, s) => (el.call('agentModeOverlayModel', s.root) || {}).kind !== 'picker',
  AgentModePickerQuery: {
    display: 'flex',
    gap: 'Z',
    paddingBottom: 'Y',
    borderBottomWidth: 'hairline',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',
    AgentModePickerPrompt: { tag: 'span', aria: { hidden: 'true' }, color: 'accentInk', text: '›' },
    AgentModePickerField: {
      flex: '1',
      display: 'flex',
      childExtends: 'AgentModeField',
      childrenAs: 'state',
      children: (el, s) => {
        const o = el.call('agentModeOverlayModel', s.root)
        return o && o.kind === 'picker' ? [o.query] : []
      }
    }
  },
  AgentModePickerEmpty: {
    padding: 'Y 0',
    color: 'caption',
    hide: (el, s) => {
      const o = el.call('agentModeOverlayModel', s.root)
      return !o || o.kind !== 'picker' || o.items.length > 0
    },
    text: (el, s) =>
      (el.call('agentModeOverlayModel', s.root) || {}).emptyText ||
      el.call('polyglot', 'workspace.agentMode.noMatches')
  },
  AgentModePickerRows: {
    role: 'listbox',
    aria: { label: (el, s) => (el.call('agentModeOverlayModel', s.root) || {}).title || '' },
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    minHeight: '0',
    maxHeight: 'G',
    paddingTop: 'Y',
    childExtends: 'AgentModePickerRow',
    childrenAs: 'state',
    children: (el, s) => {
      const o = el.call('agentModeOverlayModel', s.root)
      return o && o.kind === 'picker' ? o.items : []
    }
  }
}

export const AgentModePickerRow = {
  tag: 'div',
  role: 'option',
  aria: { selected: (el, s) => (s.selected ? 'true' : 'false') },
  display: 'flex',
  gap: 'A',
  padding: 'X Z',
  cursor: 'pointer',
  background: (el, s) => (s.selected ? 'selected' : 'transparent'),
  ':hover': { background: 'hover' },
  stateDeps: [(el, s) => !!s.selected],
  onStateUpdate: (el, s) => el.call('agentModeRevealSelected', el, s),
  opacity: (el, s) => (s.enabled ? null : '0.6'),
  onClick: (e, el, s) => el.call('agentModeClick', s.click, { double: e.detail > 1 }),
  AgentModePickerLabel: {
    flex: '1',
    minWidth: '0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: (el, s) => (s.selected ? 'title' : s.ink),
    text: (el, s) => s.label
  },
  AgentModePickerHint: {
    color: 'caption',
    whiteSpace: 'nowrap',
    hide: (el, s) => !s.hint,
    text: (el, s) => s.hint
  }
}

// A form: one row per field, then the submit button.
export const AgentModeForm = {
  tag: 'form',
  display: 'flex',
  flexDirection: 'column',
  gap: 'Z',
  hide: (el, s) => (el.call('agentModeOverlayModel', s.root) || {}).kind !== 'form',
  // Enter in a field steers through the keydown bridge; never a page submit.
  onSubmit: (e) => e.preventDefault(),
  AgentModeFormFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'Z',
    childExtends: 'AgentModeFormRow',
    childrenAs: 'state',
    children: (el, s) => {
      const o = el.call('agentModeOverlayModel', s.root)
      return o && o.kind === 'form' ? o.fields : []
    }
  },
  AgentModeFormSubmit: {
    tag: 'button',
    type: 'button',
    alignSelf: 'flex-start',
    padding: 'X Z',
    font: 'inherit',
    cursor: 'pointer',
    round: 'radiusControl',
    borderWidth: 'hairline',
    borderStyle: 'solid',
    borderColor: 'line',
    background: (el, s) =>
      (el.call('agentModeOverlayModel', s.root) || {}).submitFocused ? 'title' : 'transparent',
    color: (el, s) =>
      (el.call('agentModeOverlayModel', s.root) || {}).submitFocused ? 'document' : 'title',
    ':hover': { background: 'hover' },
    ':focus-visible': { outline: 'hairline2 solid accentInk' },
    text: (el, s) => (el.call('agentModeOverlayModel', s.root) || {}).submitLabel || '',
    onClick: (e, el, s) =>
      el.call('agentModeClick', (el.call('agentModeOverlayModel', s.root) || {}).submitClick, {
        double: false
      })
  }
}

export const AgentModeFormRow = {
  data: { agentFormField: (el, s) => s.field },
  display: 'grid',
  // The TUI's label column: one width for every row (14 cells at most).
  gridTemplateColumns: '1ch 14ch minmax(0, 1fr)',
  columnGap: 'Z',
  rowGap: 'X',
  alignItems: 'start',
  AgentModeFormMarker: {
    tag: 'span',
    aria: { hidden: 'true' },
    color: 'accentInk',
    text: (el, s) => (s.focused ? '›' : '')
  },
  AgentModeFormLabel: {
    tag: 'span',
    color: (el, s) => (s.focused ? 'title' : 'caption'),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    text: (el, s) => s.label
  },
  AgentModeFormValue: {
    display: 'flex',
    minWidth: '0',
    AgentModeFormText: {
      flex: '1',
      display: 'flex',
      hide: (el, s) => s.type !== 'text',
      childExtends: 'AgentModeField',
      childrenAs: 'state',
      children: (el, s) => (s.type === 'text' ? [s.input] : [])
    },
    AgentModeFormEditor: {
      flex: '1',
      display: 'flex',
      hide: (el, s) => s.type !== 'editor',
      childExtends: 'AgentModeTextarea',
      childrenAs: 'state',
      children: (el, s) => (s.type === 'editor' ? [s.input] : [])
    },
    AgentModeFormSelect: {
      tag: 'button',
      type: 'button',
      hide: (el, s) => s.type !== 'select',
      background: (el, s) => (s.focused ? 'selected' : 'transparent'),
      border: 'none',
      padding: '0 X',
      font: 'inherit',
      color: 'title',
      cursor: 'pointer',
      round: 'radiusControl',
      ':hover': { background: 'hover' },
      ':focus-visible': { outline: 'hairline2 solid accentInk' },
      text: (el, s) => `‹ ${s.optionLabel || '—'} ›`,
      onClick: (e, el, s) => el.call('agentModeClick', s.click, { double: e.detail > 1 })
    }
  },
  AgentModeFormError: {
    role: 'alert',
    gridColumn: '3',
    color: 'dangerInk',
    hide: (el, s) => !s.error,
    text: (el, s) => s.error
  }
}

// A multiline field (an `editor` field: in the TUI, $EDITOR). Enter makes a
// new line; ctrl+S saves the form, tab moves on (the keydown bridge).
export const AgentModeTextarea = {
  extends: 'AgentModeField',
  tag: 'textarea',
  type: null,
  rows: '4',
  resize: 'vertical',
  lineHeight: '1.5',
  padding: 'X Y',
  borderWidth: 'hairline',
  borderStyle: 'solid',
  borderColor: 'line',
  round: 'radiusControl'
}

// Yes / no.
export const AgentModeConfirm = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'A',
  hide: (el, s) => (el.call('agentModeOverlayModel', s.root) || {}).kind !== 'confirm',
  AgentModeConfirmMessage: {
    color: 'title',
    whiteSpace: 'pre-wrap',
    text: (el, s) => (el.call('agentModeOverlayModel', s.root) || {}).message || ''
  },
  AgentModeConfirmButtons: {
    display: 'flex',
    gap: 'Z',
    AgentModeChoice: {
      data: { agentAction: 'yes' },
      isChosen: (el, s) => (el.call('agentModeOverlayModel', s.root) || {}).choice === 0,
      isDanger: (el, s) => !!(el.call('agentModeOverlayModel', s.root) || {}).danger,
      text: (el, s) => (el.call('agentModeOverlayModel', s.root) || {}).yes || '',
      onClick: (e, el, s) =>
        el.call('agentModeClick', (el.call('agentModeOverlayModel', s.root) || {}).yesClick, {
          double: false
        })
    },
    AgentModeChoice_1: {
      data: { agentAction: 'no' },
      isChosen: (el, s) => (el.call('agentModeOverlayModel', s.root) || {}).choice === 1,
      text: (el, s) => (el.call('agentModeOverlayModel', s.root) || {}).no || '',
      onClick: (e, el, s) =>
        el.call('agentModeClick', (el.call('agentModeOverlayModel', s.root) || {}).noClick, {
          double: false
        })
    }
  }
}

export const AgentModeChoice = {
  tag: 'button',
  type: 'button',
  padding: 'X Z',
  font: 'inherit',
  cursor: 'pointer',
  round: 'radiusControl',
  borderWidth: 'hairline',
  borderStyle: 'solid',
  borderColor: 'line',
  background: 'transparent',
  color: 'title',
  ':hover': { background: 'hover' },
  ':focus-visible': { outline: 'hairline2 solid accentInk' },
  '.isChosen': { background: 'title', color: 'document' },
  '.isDanger': { color: 'dangerInk' }
}

// The key reference: the global keys, the view's keys, the app's actions.
// Line for line the TUI's (shell/overlays.js drawHelp): a section's title,
// its rows, a blank line — no gaps, one line each, so the model's `ui.top`
// (↓ j / ↑ k) scrolls it by whole lines. It grows to the dialog's height and
// scrolls only when the frame is shorter than the keys, like the TUI's box.
export const AgentModeHelp = {
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  minHeight: '0',
  flex: '0 1 auto',
  hide: (el, s) => (el.call('agentModeOverlayModel', s.root) || {}).kind !== 'help',
  childExtends: 'AgentModeHelpSection',
  childrenAs: 'state',
  children: (el, s) => {
    const o = el.call('agentModeOverlayModel', s.root)
    return o && o.kind === 'help' ? o.sections : []
  },
  onRender: (el) => el.call('agentModeSyncHelpScroll', el),
  stateDeps: [(el, s) => Number(s.root._agentModeRev) || 0],
  onStateUpdate: (el) => el.call('agentModeSyncHelpScroll', el),
  onScroll: (e, el) => el.call('agentModeReportHelpScroll', el)
}

export const AgentModeHelpSection = {
  tag: 'section',
  display: 'flex',
  flexDirection: 'column',
  AgentModeHelpTitle: {
    tag: 'h3',
    margin: '0',
    fontSize: 'inherit',
    fontWeight: '400',
    textTransform: 'uppercase',
    color: 'caption',
    text: (el, s) => s.title
  },
  AgentModeHelpRows: {
    tag: 'dl',
    margin: '0',
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    columnGap: 'A',
    childExtends: 'AgentModeHelpRow',
    childrenAs: 'state',
    children: (el, s) => s.rows
  },
  // The blank line after each section (drawHelp pushes one).
  AgentModeHelpBlank: { aria: { hidden: 'true' }, text: '\u00a0' }
}

export const AgentModeHelpRow = {
  display: 'contents',
  AgentModeHelpKey: { tag: 'dt', color: 'accentInk', text: (el, s) => s.k },
  // One line, cut like the TUI's truncate(): a long label never wraps.
  AgentModeHelpLabel: {
    tag: 'dd',
    margin: '0',
    minWidth: '0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: 'paragraph',
    text: (el, s) => s.label
  }
}
